(() => {
  'use strict';
  if (window.LisaHomeQuickCartLoaded) return;
  window.LisaHomeQuickCartLoaded = true;

  const homeSelector = '.merchandise-first__action[data-lisa-home-add]';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartEndpoint = root + 'cart/add.js';
  const cartJsonEndpoint = root + 'cart.js';
  const getProductCache = new Map();
  let dialog;
  let dialogReturnFocus;
  let notice;
  let noticeTimeout;
  let cartReadId = 0;
  let cartCounts = new Map();

  function updateProductBadges(cart) {
    cartCounts = new Map();
    for (const item of cart.items || []) {
      const id = String(item.product_id);
      cartCounts.set(id, (cartCounts.get(id) || 0) + Number(item.quantity || 0));
    }
    renderProductBadges();
  }

  function renderProductBadges() {
    document.querySelectorAll(homeSelector).forEach(link => {
      const card = link.closest('.merchandise-first__item, .grid__item')?.querySelector('[data-lisa-product-id]');
      const count = cartCounts.get(card?.dataset.lisaProductId) || 0;
      let badge = link.querySelector('.lisa-button-cart-count');
      let label = link.querySelector('.lisa-button-cart-count-label');
      if (!count) {
        badge?.remove();
        label?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'lisa-button-cart-count';
        badge.setAttribute('aria-hidden', 'true');
        link.appendChild(badge);
      }
      if (!label) {
        label = document.createElement('span');
        label.className = 'visually-hidden lisa-button-cart-count-label';
        link.appendChild(label);
      }
      const value = String(count);
      if (badge.textContent !== value) badge.textContent = value;
      const description = (link.dataset.lisaCartCountLabel || '[count]').replace('[count]', value);
      if (label.textContent !== description) label.textContent = description;
    });
  }

  function notify(message, error = false) {
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'lisa-home-cart-notice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.hidden = true;
      document.body.appendChild(notice);
    }
    if (noticeTimeout) clearTimeout(noticeTimeout);
    notice.replaceChildren();
    const messageElement = document.createElement('span');
    messageElement.textContent = message;
    notice.appendChild(messageElement);
    if (!error) {
      const cartLink = document.createElement('a');
      cartLink.href = root + 'cart';
      cartLink.textContent = 'View cart';
      notice.appendChild(cartLink);
    }
    notice.classList.toggle('lisa-home-cart-notice--error', error);
    notice.hidden = false;
    noticeTimeout = setTimeout(() => { notice.hidden = true; }, 6500);
  }

  function productAjaxUrl(productHref) {
    const url = new URL(productHref, location.href);
    if (url.origin !== location.origin) throw new Error('Invalid product address');
    const match = url.pathname.match(/^(.*?)(?:\/collections\/[^/]+)?\/products\/([^/]+)\/?$/);
    if (!match) throw new Error('Unable to load product details');
    return location.origin + match[1] + '/products/' + encodeURIComponent(decodeURIComponent(match[2])) + '.js';
  }

  async function fetchProduct(productHref) {
    const url = productAjaxUrl(productHref);
    if (!getProductCache.has(url)) {
      const request = fetch(url, {credentials: 'same-origin', headers: {'Accept': 'application/json'}})
        .then(async response => {
          if (!response.ok) throw new Error('Unable to load product details');
          const data = await response.json();
          if (!Array.isArray(data.variants) || !data.variants.length) throw new Error('Product choices are unavailable');
          return data;
        }).catch(error => { getProductCache.delete(url); throw error; });
      getProductCache.set(url, request);
    }
    return getProductCache.get(url);
  }

  function createDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'lisa-home-cart-dialog';
    dialog.setAttribute('aria-labelledby', 'LisaHomeCartTitle');
    dialog.innerHTML = [
      '<div class="lisa-home-cart-dialog__heading">',
      '<h2 id="LisaHomeCartTitle"></h2>',
      '<button type="button" class="lisa-home-cart-dialog__close" aria-label="Close">×</button>',
      '</div>',
      '<form class="lisa-home-cart-dialog__form">',
      '<img class="lisa-home-cart-dialog__image" alt="" hidden>',
      '<div class="lisa-home-cart-dialog__options"></div>',
      '<p class="lisa-home-cart-dialog__price"></p>',
      '<label class="lisa-home-cart-dialog__quantity">Quantity',
      '<input name="quantity" type="number" min="1" step="1" value="1" inputmode="numeric" required>',
      '</label>',
      '<p class="lisa-home-cart-dialog__error" role="alert" hidden></p>',
      '<button type="submit" class="lisa-home-cart-dialog__add">Add to cart</button>',
      '</form>'
    ].join('');
    document.body.appendChild(dialog);
    dialog.querySelector('.lisa-home-cart-dialog__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (dialogReturnFocus?.isConnected) dialogReturnFocus.focus();
      dialogReturnFocus = null;
    });
    return dialog;
  }

  function optionData(product) {
    const variants = product.variants || [];
    return (product.options || []).map((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const values = (typeof option === 'object' && Array.isArray(option.values))
        ? option.values.map(value => typeof value === 'string' ? value : value.name)
        : [...new Set(variants.map(v => (v.options || [])[index] || v['option' + (index + 1)]).filter(Boolean))];
      return {name: name || 'Option ' + (index + 1), values};
    });
  }

  function valueAt(variant, i) {
    return (Array.isArray(variant.options) && variant.options[i]) || variant['option' + (i + 1)] || '';
  }

  function formatPrice(price) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency}).format(Number(price) / 100); }
    catch { return (Number(price) / 100).toFixed(2); }
  }

  function showProductPicker(product, trigger) {
    const popup = createDialog();
    const previous = popup.querySelector('form');
    // Replacing the form prevents event handlers from an earlier product picker persisting.
    const form = previous.cloneNode(true);
    previous.replaceWith(form);
    const controls = form.querySelector('.lisa-home-cart-dialog__options');
    const qty = form.querySelector('[name="quantity"]');
    const errorLabel = form.querySelector('.lisa-home-cart-dialog__error');
    const addButton = form.querySelector('.lisa-home-cart-dialog__add');
    const priceLabel = form.querySelector('.lisa-home-cart-dialog__price');
    const image = form.querySelector('.lisa-home-cart-dialog__image');
    popup.querySelector('#LisaHomeCartTitle').textContent = product.title || 'Choose product options';
    controls.replaceChildren();
    qty.value = '1';
    errorLabel.hidden = true;
    errorLabel.textContent = '';
    addButton.textContent = 'Add to cart';

    const initial = product.variants.find(variant => variant.available) || product.variants[0];
    const selects = [];
    optionData(product).forEach((option, index) => {
      const wrapper = document.createElement('label');
      wrapper.className = 'lisa-home-cart-dialog__option';
      const title = document.createElement('span');
      title.textContent = option.name;
      wrapper.appendChild(title);
      const select = document.createElement('select');
      select.required = true;
      option.values.forEach(value => {
        const item = document.createElement('option');
        item.value = value;
        item.textContent = value;
        select.appendChild(item);
      });
      select.value = valueAt(initial, index);
      wrapper.appendChild(select);
      controls.appendChild(wrapper);
      selects.push(select);
    });

    function selectedVariant() {
      return product.variants.find(variant => selects.every((select, index) => valueAt(variant, index) === select.value));
    }
    function refresh() {
      const variant = selectedVariant();
      const available = Boolean(variant && variant.available);
      priceLabel.textContent = variant ? formatPrice(variant.price) + (available ? '' : ' · Sold out') : 'Combination unavailable';
      addButton.disabled = !available;
      addButton.textContent = available ? 'Add to cart' : 'Unavailable';
      errorLabel.hidden = true;
      const photo = variant?.featured_image?.src || product.image?.src || product.featured_image;
      const photoUrl = typeof photo === 'string' ? photo : photo?.src;
      if (photoUrl && /^https?:\/\//.test(photoUrl)) {
        image.src = photoUrl;
        image.alt = (product.title || 'Product') + ' – ' + (variant?.title || 'Selected option');
        image.hidden = false;
      } else {
        image.hidden = true;
        image.removeAttribute('src');
      }
    }
    controls.addEventListener('change', refresh);
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const variant = selectedVariant();
      const quantity = Number(qty.value);
      if (!variant?.available || !Number.isSafeInteger(quantity) || quantity < 1) {
        errorLabel.textContent = 'Select an available option and a valid quantity.';
        errorLabel.hidden = false;
        return;
      }
      addButton.disabled = true;
      addButton.textContent = 'Adding…';
      try {
        await addVariant(variant.id, quantity, dialogReturnFocus);
        popup.close();
        notify('Added to cart.');
      } catch (error) {
        errorLabel.textContent = error.message || 'Could not add to cart.';
        errorLabel.hidden = false;
      } finally {
        refresh();
      }
    });
    refresh();
    dialogReturnFocus = trigger;
    popup.showModal();
  }

  async function updateCartBubble() {
    const requestId = ++cartReadId;
    const response = await fetch(cartJsonEndpoint, {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}});
    if (!response.ok) return;
    const cart = await response.json();
    if (requestId !== cartReadId) return;
    updateProductBadges(cart);
    const icon = document.querySelector('#cart-icon-bubble');
    if (!icon) return;
    const count = Number(cart.item_count) || 0;
    let bubble = icon.querySelector('.cart-count-bubble');
    if (!count) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      icon.appendChild(bubble);
    }
    bubble.replaceChildren();
    if (count < 100) {
      const number = document.createElement('span');
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(count);
      bubble.appendChild(number);
    }
    const visuallyHidden = document.createElement('span');
    visuallyHidden.className = 'visually-hidden';
    visuallyHidden.textContent = count + (count === 1 ? ' item in cart' : ' items in cart');
    bubble.appendChild(visuallyHidden);
  }

  async function addVariant(variantId, quantity, trigger) {
    const response = await fetch(cartEndpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({id: variantId, quantity})
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Could not add this item.');
    }
    await updateCartBubble().catch(() => {});
    document.dispatchEvent(new CustomEvent('lisa:cart-added', { detail: { trigger } }));
  }

  const refreshCart = () => { updateCartBubble().catch(() => {}); };
  refreshCart();
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart(); });
  window.addEventListener('focus', refreshCart);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshCart();
  });
  const main = document.getElementById('MainContent');
  if (main) {
    let badgeUpdateQueued = false;
    new MutationObserver(records => {
      const addedCards = records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === 1 && (node.matches(homeSelector) || node.querySelector(homeSelector))
      ));
      if (!addedCards || badgeUpdateQueued) return;
      badgeUpdateQueued = true;
      requestAnimationFrame(() => { badgeUpdateQueued = false; renderProductBadges(); });
    }).observe(main, { childList: true, subtree: true });
  }

  document.addEventListener('click', async event => {
    const link = event.target.closest?.(homeSelector);
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') {
      location.assign(link.href);
      return;
    }
    link.setAttribute('aria-busy', 'true');
    try {
      const product = await fetchProduct(link.href);
      if (product.requires_selling_plan) {
        location.assign(link.href);
        return;
      }
      if (product.variants.length === 1) {
        const variant = product.variants[0];
        if (!variant.available) {
          notify('This item is sold out.', true);
        } else {
          await addVariant(variant.id, 1, link);
          notify('Added to cart.');
        }
      } else {
        showProductPicker(product, link);
      }
    } catch (error) {
      notify((error && error.message) || 'Could not add item. Please open the product page.', true);
    } finally {
      link.removeAttribute('aria-busy');
    }
  });
})();

